import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { 
  BanknotesIcon, 
  ChevronUpIcon, 
  ChevronDownIcon, 
  DocumentIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ClockIcon,
  HomeIcon,
  ShieldCheckIcon,
  BriefcaseIcon,
  UsersIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import PrequalFinancialMetricsDisplay from '../admin/PrequalFinancialMetricsDisplay';
import PrequalificationTable from '../admin/PrequalificationTable';
import { generatePrequalSummaryPDF } from '../admin/PrequalSummaryPdfGenerator';

const PrequalificationDetailsView = ({ application, onBack, customLogo }) => {
  const [prequalData, setPrequalData] = useState(null);
  const [ocrData, setOcrData] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isFinancialOverviewExpanded, setIsFinancialOverviewExpanded] = useState(true);
  const [isUploadedFilesExpanded, setIsUploadedFilesExpanded] = useState(true);
  const [isBusinessDetailsExpanded, setIsBusinessDetailsExpanded] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    const fetchPrequalData = async () => {
      if (!application || !application.id) return;
      
      try {
        // Get the full prequalification data
        const prequalDocRef = doc(db, 'prequalificationApplications', application.id);
        const prequalDocSnap = await getDoc(prequalDocRef);
        
        if (prequalDocSnap.exists()) {
          const data = { id: prequalDocSnap.id, ...prequalDocSnap.data() };
          setPrequalData(data);
          console.log("Fetched prequalData:", data);
          
          // Old logic for setting uploaded files is removed from here.
          // It will be handled by fetchUploadedFilesFromMetadata.
        }
      } catch (error) {
        console.error("Error fetching prequalification data:", error);
      }
    };

    const fetchOCRData = async () => {
      if (!application || !application.id) return;
      
      try {
        // Query OCRMetadata collection for this application
        const ocrMetadataRef = collection(db, 'OCRMetadata');
        const q = query(ocrMetadataRef, where('applicationId', '==', application.id));
        const querySnapshot = await getDocs(q);
        
        const processedData = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.documentName === "Business Tax Returns") {
            // Use improved regex to extract year from labels like "2023 Business Tax Return"
            const yearMatch = data.label.match(/\b(20\d{2})\b/);
            const year = yearMatch ? yearMatch[1] : data.label;
            console.log(`Extracted year ${year} from label: "${data.label}"`);
            processedData[year] = data.ocrResult;
          }
        });
        
        // If no data found in OCRMetadata, try the ocrData collection
        if (Object.keys(processedData).length === 0) {
          const ocrDocRef = doc(db, 'ocrData', application.id);
          const ocrDocSnap = await getDoc(ocrDocRef);
          
          if (ocrDocSnap.exists()) {
            setOcrData(ocrDocSnap.data());
          }
        } else {
          setOcrData(processedData);
        }
        
        console.log("Fetched OCR Data:", processedData);
      } catch (error) {
        console.error("Error fetching OCR data:", error);
      }
    };

    const fetchUploadedFilesFromMetadata = async () => {
      if (!application || !application.id) {
        setUploadedFiles([]); // Clear files if no application/id
        return;
      }
      try {
        const filesCollectionRef = collection(db, 'prequalificationMetadata');
        const q = query(filesCollectionRef, where('applicationId', '==', application.id));
        const querySnapshot = await getDocs(q);
        const files = [];
        querySnapshot.forEach((docSnapshot) => {
          files.push({ id: docSnapshot.id, ...docSnapshot.data() });
        });
        setUploadedFiles(files);
        console.log("Fetched uploaded files from prequalificationMetadata:", files);
      } catch (error) {
        console.error("Error fetching uploaded files from prequalificationMetadata:", error);
        setUploadedFiles([]); // Reset on error
      }
    };

    fetchPrequalData();
    fetchOCRData();
    fetchUploadedFilesFromMetadata(); // Call the new function to fetch files
  }, [application]);

  const toggleFinancialOverview = () => setIsFinancialOverviewExpanded(!isFinancialOverviewExpanded);
  const toggleUploadedFiles = () => setIsUploadedFilesExpanded(!isUploadedFilesExpanded);
  const toggleBusinessDetails = () => setIsBusinessDetailsExpanded(!isBusinessDetailsExpanded);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(value);
  };

  const handleGenerateSummaryPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      // Prepare data for PDF generation
      const pdfData = {
        businessName: prequalData?.businessName || application?.businessName || 'N/A',
        businessDescription: prequalData?.businessDescription || application?.businessDescription || '',
        purchasePrice: prequalData?.purchasePrice || application?.purchasePrice || 0,
        prequalifiedAmount: prequalData?.prequalifiedAmount || application?.prequalifiedAmount || 0,
        prequalificationType: prequalData?.prequalificationType || application?.prequalificationType || 'Easy Financing',
        prequalificationWithSalary: prequalData?.prequalificationWithSalary !== undefined 
          ? prequalData.prequalificationWithSalary 
          : (application?.prequalificationWithSalary || false),
        ocrData: ocrData || {},
        logoUrl: customLogo || null
      };
      
      console.log("Generating PDF with data:", pdfData);
      
      // Generate PDF blob
      const blob = await generatePrequalSummaryPDF(pdfData);
      
      // Create a download URL
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `${pdfData.businessName.replace(/\s+/g, '_')}_Prequalification_Summary.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsGeneratingPDF(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setIsGeneratingPDF(false);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <main className="py-10">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Header with back button */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Prequalification Details
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                View and manage prequalification information for {prequalData?.businessName || application?.businessName || 'this business'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onBack}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>
              
              {/* PDF Download Button */}
              <button
                onClick={handleGenerateSummaryPDF}
                disabled={isGeneratingPDF || !prequalData}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Download Summary
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Prequalification Amount Card */}
          <div className="bg-gradient-to-br from-white to-green-50 shadow-lg rounded-xl overflow-hidden mb-8 border border-green-100">
            <div className="px-6 py-8 sm:px-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold leading-7 text-gray-900 flex items-center">
                    <BuildingOfficeIcon className="h-6 w-6 text-indigo-600 mr-2" />
                    {prequalData?.businessName || application?.businessName || 'Business'} 
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Prequalification Status: <span className="font-medium text-indigo-600">Approved</span>
                  </p>
                </div>
                
                <div className="mt-4 sm:mt-0">
                  <div className="flex items-center justify-center sm:justify-end">
                    <div className="bg-white rounded-lg shadow-sm border border-green-200 px-6 py-4">
                      <p className="text-sm text-gray-500 mb-1">Prequalified Amount</p>
                      <p className="text-3xl font-bold text-green-600">
                        {formatCurrency(prequalData?.prequalifiedAmount || application?.prequalifiedAmount || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                  {prequalData?.prequalificationType || application?.prequalificationType || 'Standard Financing'}
                </span>
                {(prequalData?.prequalificationWithSalary || application?.prequalificationWithSalary) && (
                  <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                    With $75k Salary
                  </span>
                )}
                <span className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-sm font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                  SBA Loan
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Business Details */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Business Details</h3>
                </div>
                <button
                  onClick={toggleBusinessDetails}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isBusinessDetailsExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isBusinessDetailsExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Business name</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {prequalData?.businessName || application?.businessName || 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Asking price
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 font-medium">
                        {formatCurrency(prequalData?.purchasePrice || application?.purchasePrice || 0)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Business Age (Years)
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData?.businessAge || 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <HomeIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Real Estate Included
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {prequalData?.realEstateIncluded ? 'Yes' : 'No'}
                      </dd>
                    </div>
                    {prequalData?.realEstateIncluded && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Real Estate Value
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 font-medium">
                          {formatCurrency(prequalData?.realEstateValue || 0)}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <ShieldCheckIcon className="h-4 w-4 text-gray-400 mr-1" />
                        License Needed
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData?.licenseNeeded === 'boolean' ? (prequalData.licenseNeeded ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <BriefcaseIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Multiple LLCs
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData?.hasMultipleLLCs === 'boolean' ? (prequalData.hasMultipleLLCs ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <UsersIcon className="h-4 w-4 text-gray-400 mr-1" />
                        Top Customer Concentration (&gt;15%)
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {typeof prequalData?.topCustomerConcentration === 'boolean' ? (prequalData.topCustomerConcentration ? 'Yes' : 'No') : 'N/A'}
                      </dd>
                    </div>
                  </dl>

                  {/* Description Section - Moved and restyled */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-2 text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-line">
                      {prequalData?.businessDescription || application?.businessDescription || 'No description provided.'}
                    </dd>
                  </div>

                  {/* Broker Notes Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <dt className="text-sm font-medium text-gray-500 flex items-center">
                      <PencilSquareIcon className="h-4 w-4 text-gray-400 mr-1" />
                      Broker Notes
                    </dt>
                    <dd className="mt-2 text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-line">
                      {prequalData?.brokerNotes || application?.brokerNotes || 'No notes provided.'}
                    </dd>
                  </div>
                </div>
              )}
            </div>
              
            {/* Financial Overview */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Financial Overview</h3>
                </div>
                <button
                  onClick={toggleFinancialOverview}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isFinancialOverviewExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isFinancialOverviewExpanded && (
                <div className="border-t border-gray-100 px-6 py-5">
                  {Object.keys(ocrData).length > 0 ? (
                    <>
                      <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <PrequalFinancialMetricsDisplay 
                          ocrData={ocrData} 
                          loanData={prequalData || application || {}} 
                        />
                      </div>
                      
                      <div className="mt-6 overflow-x-auto">
                        <PrequalificationTable 
                          ocrData={ocrData} 
                          loanData={prequalData || application || {}} 
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <ChartBarIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No financial data available for this prequalification.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Uploaded Files */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center">
                  <ClipboardDocumentListIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-semibold leading-7 text-gray-900">Uploaded Files</h3>
                </div>
                <button
                  onClick={toggleUploadedFiles}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  {isUploadedFilesExpanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {isUploadedFilesExpanded && (
                <div className="border-t border-gray-100">
                  {uploadedFiles && uploadedFiles.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                      {uploadedFiles.map((file, index) => (
                        <div 
                          key={file.id || file.fileId || index} 
                          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-indigo-50 rounded-lg">
                              <DocumentIcon className="h-5 w-5 text-indigo-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.fileName || file.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {file.label || file.documentName || 'Document'}
                              </p>
                              <div className="mt-2">
                                <a
                                  href={file.fileUrl || file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                >
                                  View Document
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <DocumentIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500 italic">No files uploaded yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrequalificationDetailsView;